"use client";
import type { VariantProps } from "class-variance-authority";
import type { ShortcutId } from "./shortcuts";
import { cva } from "class-variance-authority";
import cn from "twsx";
import { useShortcutKeycaps } from "./useShortcut";

// Keyboard hints (Figma "Kbd"): inline text in menus, boxed in tooltips and
// comboboxes, taller in the command palette. Chords sit 3px apart.
const kbdVariants = cva("inline-flex items-center justify-center font-sans text-xs text-text-tertiary", {
  variants: {
    variant: {
      inline: "font-medium",
      box: "h-[19px] min-w-4.5 rounded-kbd border border-border-subtle px-0.75 leading-[13px]",
      palette: "h-[23px] min-w-5 rounded-checkbox border border-border-subtle px-1 font-[450]",
    },
  },
  defaultVariants: { variant: "box" },
});

export type KbdProps = VariantProps<typeof kbdVariants> & { className?: string } & (
  | { keys: string | string[]; shortcut?: never }
  | { shortcut: ShortcutId; keys?: never }
);

function KbdKeys({ keys, variant, className }: VariantProps<typeof kbdVariants> & { keys: string[]; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-0.75", className)}>
      {keys.map((key, index) => (
        <kbd key={`${key}-${index}`} className={kbdVariants({ variant })}>{key}</kbd>
      ))}
    </span>
  );
}

function ShortcutKeys({ shortcut, variant, className }: VariantProps<typeof kbdVariants> & { shortcut: ShortcutId; className?: string }) {
  return <KbdKeys keys={useShortcutKeycaps(shortcut)} variant={variant} className={cn("max-md:hidden", className)} />;
}

/** Use when showing a keyboard shortcut; pass `shortcut` for a registry shortcut so the hint matches its binding. */
export function Kbd({ keys, shortcut, variant, className }: KbdProps) {
  if (shortcut) return <ShortcutKeys shortcut={shortcut} variant={variant} className={className} />;
  return <KbdKeys keys={[keys ?? []].flat()} variant={variant} className={className} />;
}
